import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Tags } from 'lucide-react';
import type { ContributorCategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useContributorCategoriesList, useContributorCategoryMutations } from '~/hooks/use-contributor-categories';
import { ContributorCategoryEditDialog } from './category-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Contributor categories (Κατηγορίες Συντελεστών) CRUD surface — parity with the legacy admin grid. */
export function ContributorCategoriesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useContributorCategoriesList();
	const { remove } = useContributorCategoryMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<ContributorCategoryToken | null>(null);
	const [deleteRow, setDeleteRow] = useState<ContributorCategoryToken | null>(null);

	const columns = useMemo<SystemGridColumn<ContributorCategoryToken>[]>(
		() => [
			{
				key: 'name',
				header: t('contributors.categories.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('contributors.categories.columns.code'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'color',
				header: t('contributors.categories.columns.color'),
				width: '120px',
				sortValue: o => o.chex ?? '',
				exportValue: o => o.chex ?? '',
				render: o =>
					o.chex ? (
						<span className="inline-flex items-center gap-2">
							<span className="inline-block h-4 w-4 rounded-full border border-border" style={{ backgroundColor: o.chex }} title={o.chex} />
							<span className="font-mono text-xs text-foreground-muted">{o.chex}</span>
						</span>
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'assignToProduct',
				header: t('contributors.categories.columns.assignToProduct'),
				width: '140px',
				align: 'center',
				sortValue: o => (o.ass_to_prd ? 1 : 0),
				exportValue: o => (o.ass_to_prd ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.ass_to_prd)} />
			},
			{
				key: 'visible',
				header: t('contributors.categories.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('contributors.categories.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ContributorCategoryToken>
				gridId="contributor-categories"
				title={t('contributors.categories.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm ?? ''}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Tags size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('contributors.categories.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="contributor-categories"
			/>

			<ContributorCategoryEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('contributors.categories.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('contributors.categories.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
